/**
 * AI画像生成 hook
 */
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useCallback, useRef } from "react";
import { Asset } from "app/types";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { useAssetCache, useParentAssetCache } from "common/hook";
import { PlanType } from "features/subscription/types";
import { generateImageSSE } from "../api";
import { GenerationStatus, AIGenerateOptions, AIImageResult, ImagePurpose } from "../types";

interface UseAIImageGenerationProps {
  /** 対象ユーザーUID（プロフィール用はなしでも可） */
  uid?: string;
  /** 生成目的（デフォルト: "child_profile_icon"） */
  purpose?: ImagePurpose;
}

interface UseAIImageGenerationReturn {
  status: GenerationStatus;
  progress: number;
  partialImage: string | null;
  results: AIImageResult[];
  errorMessage: string | null;
  generate: (options: AIGenerateOptions) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

export const useAIImageGeneration = ({
  uid,
  purpose = "child_profile_icon",
}: UseAIImageGenerationProps): UseAIImageGenerationReturn => {
  const { onAssetSaved } = useAssetCache();
  const { onParentAssetSaved } = useParentAssetCache();
  // purpose で保護者用かどうかを判断
  const isParentPurpose = purpose === "parent_profile_icon";
  const plan = useSelector((state: RootState) => state.subscription.plan);
  const isPaidPlan =
    plan === PlanType.STANDARD || plan === PlanType.PREMIUM || plan === PlanType.BUILDER;
  const [status, setStatus] = useState<GenerationStatus>(GenerationStatus.IDLE);
  const [progress, setProgress] = useState(0);
  const [partialImage, setPartialImage] = useState<string | null>(null);
  const [results, setResults] = useState<AIImageResult[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generate = useCallback(
    async (options: AIGenerateOptions) => {
      abortControllerRef.current = new AbortController();
      setStatus(GenerationStatus.GENERATING);
      setProgress(0);
      setPartialImage(null);
      setResults([]);
      setErrorMessage(null);

      try {
        await generateImageSSE({
          uid,
          options,
          purpose,
          signal: abortControllerRef.current.signal,
          callbacks: {
            onStart: () => {
              setProgress(0);
            },
            onPartialImage: (
              b64Json: string,
              prog: number,
              _index: number,
              _imageIndex?: number,
            ) => {
              setProgress(prog);
              setPartialImage(`data:image/webp;base64,${b64Json}`);
            },
            onComplete: (_imageUrl: string, _id: number, _fileSize: number) => {
              // all_complete で処理するため、ここでは何もしない
            },
            onAllComplete: (assets, _usage) => {
              setStatus(GenerationStatus.COMPLETE);
              setProgress(100);
              setPartialImage(null);

              // 結果を設定
              const finalResults: AIImageResult[] = assets.map((asset) => ({
                id: asset.id,
                url: asset.url,
                fileSize: asset.file_size,
              }));
              setResults(finalResults);

              // UIDがある場合のみキャッシュ更新
              if (uid && assets.length > 0) {
                const asset = assets[0];
                const newAsset: Asset = {
                  id: asset.id,
                  uid: asset.uid || uid,
                  type: asset.type,
                  name: asset.name,
                  tags: asset.tags,
                  content_type: asset.content_type,
                  file_size: asset.file_size,
                  metadata: asset.metadata as Asset["metadata"],
                  url: asset.url,
                  created_at: asset.created_at,
                };
                // purpose に応じて適切なキャッシュ更新メソッドを使用
                if (isParentPurpose) {
                  onParentAssetSaved(uid, newAsset);
                } else {
                  onAssetSaved(uid, newAsset);
                }
              }
            },
            onError: (code: string, message: string) => {
              setStatus(GenerationStatus.ERROR);
              console.error("[AI Generation Error]", code, message);
              // エラーコードに応じたメッセージ表示
              if (code === "QUOTA_EXCEEDED" || code === "LIMIT_EXCEEDED") {
                setErrorMessage(I18n.t("MSG_TOAST_AI_QUOTA_EXCEEDED"));
                toast.error(
                  <div>
                    <p className="text text-textcolor-black">
                      {I18n.t("MSG_TOAST_AI_QUOTA_EXCEEDED")}
                    </p>
                  </div>,
                );
              } else if (code === "ACCESS_DENIED") {
                setErrorMessage(I18n.t("MSG_TOAST_AI_ACCESS_DENIED"));
                toast.error(
                  <div>
                    <p className="text text-textcolor-black">
                      {I18n.t("MSG_TOAST_AI_ACCESS_DENIED")}
                    </p>
                  </div>,
                );
              } else {
                setErrorMessage(message);
                toast.error(
                  <div>
                    <p className="text text-textcolor-black">
                      {I18n.t("MSG_TOAST_AI_GENERATION_FAILED")}
                    </p>
                  </div>,
                );
              }
            },
          },
        });
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          setStatus(GenerationStatus.IDLE);
          setProgress(0);
          setPartialImage(null);
        } else {
          setStatus(GenerationStatus.ERROR);
          const errorMsg = (error as Error).message || "Unknown error";
          setErrorMessage(errorMsg);
          console.error("[AI Generation Error]", error);
          toast.error(
            <div>
              <p className="text text-textcolor-black">
                {I18n.t("MSG_TOAST_AI_GENERATION_FAILED")}
              </p>
            </div>,
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uid, purpose, onAssetSaved, onParentAssetSaved, isParentPurpose, isPaidPlan],
  );

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStatus(GenerationStatus.IDLE);
    setProgress(0);
    setPartialImage(null);
  }, []);

  const reset = useCallback(() => {
    cancel();
    setResults([]);
    setErrorMessage(null);
  }, [cancel]);

  return {
    status,
    progress,
    partialImage,
    results,
    errorMessage,
    generate,
    cancel,
    reset,
  };
};
